import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { Shield, Eye, EyeOff, Loader, Building2, User, Mail, Lock, Check } from "lucide-react";

export default function SignupPage() {
  const { user, signup } = useAuth();
  const [form, setForm] = useState({ company_name: "", admin_name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await signup(form);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  // Password strength visual
  const checks = {
    length: form.password.length >= 10,
    upper: /[A-Z]/.test(form.password),
    lower: /[a-z]/.test(form.password),
    digit: /\d/.test(form.password),
    special: /[^A-Za-z0-9]/.test(form.password),
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary text-primary-foreground rounded-lg flex items-center justify-center">
            <Shield size={20} />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">DATON</h2>
            <p className="text-xs text-muted-foreground">IT-Support & Kundenverwaltung</p>
          </div>
        </div>

        <div className="bg-card border border-border shadow-soft p-6">
          <h1 className="text-xl font-semibold mb-1">Workspace erstellen</h1>
          <p className="text-sm text-muted-foreground mb-6">Eigener Workspace für deine Firma — komplett isoliert</p>

          <form onSubmit={submit} className="space-y-4">
            <Field icon={Building2} label="Firmenname" required value={form.company_name}
              onChange={v => setForm({ ...form, company_name: v })} placeholder="RM Informatik" testId="signup-company" />
            <Field icon={User} label="Dein Name" required value={form.admin_name}
              onChange={v => setForm({ ...form, admin_name: v })} placeholder="Max Muster" testId="signup-name" />
            <Field icon={Mail} type="email" label="E-Mail" required value={form.email}
              onChange={v => setForm({ ...form, email: v })} placeholder="max@firma.ch" testId="signup-email" />

            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                <Lock size={11} /> Passwort
              </label>
              <div className="relative">
                <input
                  data-testid="signup-password"
                  type={showPw ? "text" : "password"}
                  required value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2.5 pr-10 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  placeholder="Mindestens 10 Zeichen"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {form.password && (
                <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                  <Strength label="≥ 10 Zeichen" ok={checks.length} />
                  <Strength label="Grossbuchstabe" ok={checks.upper} />
                  <Strength label="Kleinbuchstabe" ok={checks.lower} />
                  <Strength label="Zahl" ok={checks.digit} />
                  <Strength label="Sonderzeichen" ok={checks.special} />
                </div>
              )}
            </div>

            {error && (
              <div data-testid="signup-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20">
                {error}
              </div>
            )}

            <button data-testid="signup-submit" type="submit" disabled={loading}
              className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader size={15} className="animate-spin" />}
              {loading ? "Workspace wird erstellt..." : "Workspace erstellen & loslegen"}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              Schon einen Account? <Link to="/login" className="text-primary hover:underline font-semibold">Anmelden</Link>
            </p>
          </form>
        </div>

        <p className="text-center mt-4 text-xs text-muted-foreground">
          Mit der Registrierung bekommst du eine eigene, isolierte Datenbank für deine Firma.
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", required, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </label>
      <input data-testid={testId} type={type} required={required} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none transition-colors" />
    </div>
  );
}

function Strength({ label, ok }) {
  return (
    <div className={`flex items-center gap-1 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/60"}`}>
      <Check size={9} className={ok ? "" : "opacity-30"} /> {label}
    </div>
  );
}
